import { FieldKey } from '../config/documentFieldSources';
import { HebergementUploads, UploadedDocResult } from '../store/useFormCGIAStore';
import { isValidNumeroFormule } from '../validation/numeroFormule';
import { toStorage } from '../../../utils/frPhone';
import { normalizeTypeVoie } from '../../../utils/normalizeTypeVoie';

/**
 * Convertit l'état interne de FormCGIA (valeurs formulaire + URLs Firebase des
 * docs uploadés) vers le payload exact attendu par `createCGController`
 * (`POST /api/cg`). Doit correspondre au format produit par `CGForm.tsx` lignes
 * 1025-1055.
 *
 * ⚠️  La clé : la fonction `createCG` côté backend attend des URLs de fichiers
 * stringifiées en JSON dans les champs `sellerFilesStructured`, `buyerFilesStructured`,
 * etc. On reconstruit ces structures depuis `uploadedDocs`.
 */

interface AgencyInfo {
  agencyID: number | string | undefined;
  personID: number | string | undefined;
  agencyEmail: string | undefined;
  agencyName: string | undefined;
  agencyAddress: string | undefined;
}

interface MapParams {
  formValues: Record<FieldKey, unknown>;
  uploadedDocs: Partial<Record<string, UploadedDocResult>>;
  agencyInfo: AgencyInfo;
  hebergementUploads?: HebergementUploads;
  kbisUrls?: string[];
  /** Pièces du co-titulaire (uploadées sans OCR) — deviennent coBuyerFilesUrls. */
  coBuyerFiles?: string[];
  /** Commentaire libre de l'agence (persisté en external_comments côté backend). */
  agencyComment?: string;
  /** Fichiers libres joints au commentaire agence (sans OCR). */
  agencyCommentFiles?: string[];
  /**
   * TOUTES les URLs Firebase déposées pendant la session form-cgia, quel que
   * soit leur sort (représentant d'un slot, verso, doublon, échec d'analyse).
   *
   * Les buckets structurés ci-dessous sont construits par LISTE BLANCHE de
   * slots : tout ce qui n'y figure pas était purement et simplement perdu avant
   * `CG_Documents`, donc invisible dans /review et dans les modales
   * « Soumettre la DC / le CTVO » — et hors périmètre d'archivage CFN.
   * Mesuré en prod le 2026-08-05 : 197 fichiers sur 1238 (68 % des dossiers).
   *
   * Le reliquat part en `otherFilesUrls`. Aucun bucket structuré n'est modifié :
   * ce qui est soumis à l'ANTS est strictement inchangé.
   */
  allUploadedUrls?: string[];
  /**
   * Le routage des identités (personne × face, `identityRouting`) a arbitré les
   * slots de ce dossier : les `formValues` acheteur en sont issus (avec les
   * références 13750 + cession + mandat et l'équivalence nom d'usage). Le filet
   * historique `pickAcquereurIdentityFromDocs` — scoring plus faible (cession +
   * mandat seulement) — ne doit alors PAS les écraser à la soumission.
   */
  identityRouted?: boolean;
}

const urlsOf = (doc: UploadedDocResult | undefined): string[] => doc?.firebaseUrls ?? [];

const docFieldStr = (doc: UploadedDocResult | undefined, key: string): string | null => {
  const v = (doc?.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined)?.[key]?.value;
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
};

const normName = (v: unknown): string =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Départage l'identité du TITULAIRE acheteur quand DEUX pièces d'identité
 * coexistent (miroir de `reconcileAcquereurIdentity` côté /review + de
 * `pickAcquereurPi`). Le slot `piece_identite` n'est pas forcément le titulaire :
 * on retient la CNI dont le nom + prénom matche l'acheteur de la CESSION OU le
 * mandant du MANDAT (prénom prioritaire). Retourne l'identité à utiliser, ou
 * null si le slot `piece_identite` est déjà le bon / indéterminable.
 */
const pickAcquereurIdentityFromDocs = (
  uploadedDocs: Partial<Record<string, UploadedDocResult>>,
): { nom: string | null; prenom: string | null; nom_usage: string | null } | null => {
  const pi = uploadedDocs.piece_identite;
  const piHeb = uploadedDocs.piece_identite_hebergeur;
  if (!pi || !piHeb) return null;

  const refs = [
    {
      nom: docFieldStr(uploadedDocs.cerfa_cession, 'acheteur_nom'),
      prenom: docFieldStr(uploadedDocs.cerfa_cession, 'acheteur_prenom'),
    },
    {
      nom: docFieldStr(uploadedDocs.mandat, 'mandant_nom'),
      prenom: docFieldStr(uploadedDocs.mandat, 'mandant_prenom'),
    },
  ].filter((r) => r.nom || r.prenom);
  if (refs.length === 0) return null;

  const scoreVs = (doc: UploadedDocResult, ref: { nom: string | null; prenom: string | null }): number => {
    const nomOk = Boolean(
      ref.nom && docFieldStr(doc, 'nom') && normName(ref.nom) === normName(docFieldStr(doc, 'nom')),
    );
    const prenomOk = Boolean(
      ref.prenom && docFieldStr(doc, 'prenom') && normName(ref.prenom) === normName(docFieldStr(doc, 'prenom')),
    );
    return (prenomOk ? 2 : 0) + (nomOk ? 1 : 0);
  };
  const score = (doc: UploadedDocResult): number => Math.max(0, ...refs.map((r) => scoreVs(doc, r)));

  // On ne bascule que si l'autre CNI matche STRICTEMENT mieux le titulaire.
  if (score(piHeb) > score(pi)) {
    return {
      nom: docFieldStr(piHeb, 'nom'),
      prenom: docFieldStr(piHeb, 'prenom'),
      nom_usage: docFieldStr(piHeb, 'nom_usage'),
    };
  }
  return null;
};

export const mapStoreToCreateCgPayload = ({
  formValues,
  uploadedDocs,
  agencyInfo,
  hebergementUploads,
  kbisUrls,
  coBuyerFiles,
  agencyComment,
  agencyCommentFiles,
  identityRouted,
  allUploadedUrls,
}: MapParams) => {
  // Si l'agence a détecté un co-titulaire, on garde `cobuyer: 'yes'`.
  const cobuyerPresent = Boolean(formValues.cobuyer_present);

  // Personne morale : la raison sociale va dans CompanyName (pas Nom/LastName), le
  // SIRET (14, prioritaire sur le SIREN 9) dans Siret, et Nom/Prénom restent vides.
  // Sinon (particulier) : Nom/Prénom remplis, CompanyName/Siret vides. Corrige le cas
  // où l'OCR met la raison sociale d'une société dans le champ « nom » du titulaire CG.
  const sellerIsPro = (formValues.seller_client_type as string) === 'pro';
  const buyerIsPro = (formValues.buyer_client_type as string) === 'pro';

  /**
   * La carte grise écrit la raison sociale d'une société dans la case « nom » du
   * titulaire : d'où le repli `raison sociale ← nom` ci-dessous. Mais ce repli
   * est un couteau à double tranchant — appliqué à une partie devenue `pro` sur
   * un signal faible, il transforme le NOM D'UNE PERSONNE PHYSIQUE en raison
   * sociale, de façon irréversible sans repasser par la review.
   *
   * On ne l'autorise donc que lorsqu'un identifiant d'entreprise INDÉPENDANT du
   * nom a été lu (SIRET ou SIREN). Sans identifiant, la raison sociale ne peut
   * venir que d'un champ qui la nomme explicitement.
   */
  const sellerAUnIdentifiant = Boolean(formValues.seller_siret || formValues.seller_siren);
  const buyerAUnIdentifiant = Boolean(formValues.buyer_siret);

  // Départage titulaire acheteur (2 CNI) : si une CNI matche mieux l'acheteur de
  // la cession / le mandant, son identité prime sur le slot piece_identite brut
  // (retour Biwiz/François — ne pas soumettre l'identité du co-titulaire).
  // ⚠ Uniquement en FALLBACK : quand le routage (personne × face) a arbitré les
  // slots, les formValues sont déjà justes — ce filet, au scoring plus faible,
  // pouvait les écraser avec la mauvaise CNI (unification retours 2026-07-29).
  const reconciledBuyer = identityRouted ? null : pickAcquereurIdentityFromDocs(uploadedDocs);
  const buyerFirstName = reconciledBuyer?.prenom ?? (formValues.buyer_prenom as string) ?? '';
  const buyerLastName = reconciledBuyer?.nom ?? (formValues.buyer_nom as string) ?? '';
  const buyerUsualName = reconciledBuyer?.nom_usage ?? (formValues.buyer_nom_usage as string) ?? '';

  // Formulaire flatten compatible avec le schéma existant
  const flatForm: Record<string, unknown> = {
    // Véhicule
    regPlate: formValues.immatriculation ?? '',
    vin: formValues.vin ?? '',
    // Ne JAMAIS pousser un n° de formule hors format SIV ("20xx…") vers MiSIV : l'OCR
    // "invente" parfois une valeur sur une autre zone de la CG (ex "F2AX28QT2HZAA0518",
    // GH-828-XX) quand le coupon détachable est retiré. On le vide → le champ reste à
    // compléter depuis le verso de la CG (cf. règle numero_formule_coupon_detache).
    numFormule: isValidNumeroFormule(formValues.numero_formule)
      ? String(formValues.numero_formule).replace(/\s+/g, '').toUpperCase()
      : '',
    purchaseDateTime: formValues.date_vente ?? '', // datetime-local (YYYY-MM-DDTHH:mm)

    // Vendeur (ancien titulaire)
    sellerClientType: (formValues.seller_client_type as string) || 'person',
    sellerGender: sellerIsPro ? '' : (formValues.seller_civilite ?? ''),
    sellerFirstName: sellerIsPro ? '' : (formValues.seller_prenom ?? ''),
    sellerLastName: sellerIsPro ? '' : (formValues.seller_nom ?? ''),
    sellerUsualName: sellerIsPro ? '' : (formValues.seller_nom_usage ?? ''),
    // SIRET (14) prioritaire sur le SIREN (9) ; raison sociale via fallback seller_nom
    // (la CG met la raison sociale d'une PM dans le champ « nom » du titulaire).
    sellerSiret: sellerIsPro ? formValues.seller_siret || formValues.seller_siren || '' : '',
    sellerCompanyName: sellerIsPro
      ? formValues.seller_raison_sociale || (sellerAUnIdentifiant ? formValues.seller_nom : '') || ''
      : '',

    // Nouveau titulaire
    buyerClientType: (formValues.buyer_client_type as string) || 'person',
    buyerGender: buyerIsPro ? '' : (formValues.buyer_civilite ?? ''),
    buyerFirstName: buyerIsPro ? '' : buyerFirstName,
    buyerLastName: buyerIsPro ? '' : buyerLastName,
    buyerUsualName: buyerIsPro ? '' : buyerUsualName,
    buyerSiret: buyerIsPro ? formValues.buyer_siret || '' : '',
    buyerCompanyName: buyerIsPro
      ? formValues.buyer_raison_sociale || (buyerAUnIdentifiant ? formValues.buyer_nom : '') || ''
      : '',

    buyerAddressNumber: formValues.buyer_numero_voie ?? '',
    buyerAddressExtension: formValues.buyer_extension_voie ?? '',
    // L'OCR remonte le libellé long ("BOULEVARD") ; le backend, le Select
    // SubmitCTVO et MiSIV attendent le code court ("blvd"). Cf.
    // packages/frontend/src/utils/normalizeTypeVoie.ts.
    buyerAddressType: normalizeTypeVoie(formValues.buyer_type_voie as string | undefined),
    buyerAddressName: formValues.buyer_nom_voie ?? '',
    buyerAddressPlace: formValues.buyer_lieu_dit ?? '',
    buyerAddressStairs: formValues.buyer_etage_escalier ?? '',
    buyerAddressComp: formValues.buyer_complement_adresse ?? '',
    buyerAddressPostalBox: formValues.buyer_boite_postale ?? '',
    buyerCity: formValues.buyer_ville ?? '',
    buyerPostalCode: formValues.buyer_code_postal ?? '',
    buyerCountry: formValues.buyer_pays ?? 'FRANCE',

    buyerResidenceType: (formValues.buyer_residence_type as string) || 'owner',
    buyerDateOfBirth: formValues.buyer_date_naissance ?? '',
    buyerPlaceOfBirth: formValues.buyer_lieu_naissance ?? '',
    buyerCountryOfBirth: formValues.buyer_pays_naissance ?? 'FRANCE',
    buyerDepartmentOfBirth: formValues.buyer_departement_naissance ?? '',
    // Téléphone acheteur normalisé au format FR (0033…/+33… → 06/07) avant MiSIV.
    buyerPhone: toStorage(formValues.buyer_telephone),
    buyerDocumentType: 'cni',

    // Co-titulaire
    cobuyer: cobuyerPresent ? 'yes' : 'no',
    coBuyerClientType: (formValues.cobuyer_client_type as string) || 'person',
    coBuyerGender: formValues.cobuyer_civilite ?? '',
    coBuyerFirstName: formValues.cobuyer_prenom ?? '',
    coBuyerLastName: formValues.cobuyer_nom ?? '',
    coBuyerUsualName: formValues.cobuyer_nom_usage ?? '',
    coBuyerSiret: formValues.cobuyer_siret ?? '',
    coBuyerCompanyName: formValues.cobuyer_raison_sociale ?? '',
  };

  // Structures de fichiers attendues par le backend (clés strictes)
  const sellerFilesStructured = {
    carteGrise: urlsOf(uploadedDocs.carte_grise),
    cerfaCession: urlsOf(uploadedDocs.cerfa_cession),
    cerfaDemandeImmat: urlsOf(uploadedDocs.cerfa_demande_immat),
    mandat: urlsOf(uploadedDocs.mandat),
    assurance: urlsOf(uploadedDocs.assurance),
    controleTechnique: urlsOf(uploadedDocs.controle_technique),
    // Le Kbis d'un vendeur professionnel n'avait jusqu'ici AUCUN rangement côté
    // vendeur : déposé, il finissait dans le fourre-tout `otherFiles`.
    kbis: urlsOf(uploadedDocs.kbis_vendeur),
  };

  const isBuyerPro = (formValues.buyer_client_type as string) === 'pro';
  // Routage (personne × type × face) : chaque face occupe son propre slot —
  // on concatène recto + verso pour que les DEUX fichiers partent au backend.
  const buyerFilesStructured = {
    identity: [...urlsOf(uploadedDocs.piece_identite), ...urlsOf(uploadedDocs.piece_identite_verso)],
    license: isBuyerPro
      ? (kbisUrls ?? [])
      : [...urlsOf(uploadedDocs.permis_conduire), ...urlsOf(uploadedDocs.permis_conduire_verso)],
    // Le Kbis acquéreur squattait le bucket `license` (celui du permis) via
    // `kbisUrls`, alimenté par le seul parcours legacy. En v2 il a désormais sa
    // propre clé ; `license` reste tel quel pour ne pas casser l'existant.
    kbis: [...urlsOf(uploadedDocs.kbis_acheteur), ...urlsOf(uploadedDocs.kbis)],
  };

  // Hébergement : si mode guest, les URLs viennent du store (uploads inline
  // dans ReviewFormStep). Si mode owner, justif_domicile du wizard.
  const isGuest = (formValues.buyer_residence_type as string) === 'guest';
  const buyerResidencyFilesStructured = isGuest
    ? {
        hostProofOfResidence: hebergementUploads?.hostProofOfResidence ?? [],
        hostIdentity: hebergementUploads?.hostIdentity ?? [],
        honorAttestation: hebergementUploads?.honorAttestation ?? [],
        guestProofOfResidence: hebergementUploads?.guestProofOfResidence ?? [],
      }
    : {
        hostProofOfResidence: [],
        hostIdentity: [],
        honorAttestation: [],
        guestProofOfResidence: urlsOf(uploadedDocs.justif_domicile),
      };

  // Concat des URLs legacy pour rétro-compatibilité backend
  const sellerFilesUrls = [
    ...sellerFilesStructured.carteGrise,
    ...sellerFilesStructured.cerfaCession,
    ...sellerFilesStructured.cerfaDemandeImmat,
    ...sellerFilesStructured.mandat,
    ...sellerFilesStructured.assurance,
    ...sellerFilesStructured.controleTechnique,
    ...sellerFilesStructured.kbis,
  ];
  const buyerFilesUrls = [
    ...buyerFilesStructured.identity,
    ...buyerFilesStructured.license,
    ...buyerFilesStructured.kbis,
  ];
  const buyerResidencyFilesUrls = urlsOf(uploadedDocs.justif_domicile);
  // Pièces du co-titulaire : sous-bloc dédié + pièces routées vers les slots
  // co-titulaire par identityRouting (une CNI du co-titulaire déposée dans la
  // zone générale est matchée sur le Cerfa 13750 et doit partir avec l'entité
  // CoBuyer, recto ET verso). Dédupliquées : un fichier du sous-bloc dédié est
  // AUSSI routé dans un slot co-titulaire.
  const coBuyerFilesUrls: string[] = Array.from(
    new Set([
      ...(coBuyerFiles ?? []),
      ...urlsOf(uploadedDocs.piece_identite_cotitulaire),
      ...urlsOf(uploadedDocs.piece_identite_cotitulaire_verso),
      ...urlsOf(uploadedDocs.permis_conduire_cotitulaire),
      ...urlsOf(uploadedDocs.permis_conduire_cotitulaire_verso),
    ]),
  );

  // Reliquat : tout fichier déposé qui n'a atterri dans AUCUN bucket structuré.
  // On part des structures elles-mêmes (et non des concats legacy) pour ne pas
  // compter en double les pièces d'hébergement, absentes de
  // `buyerResidencyFilesUrls`. Les PJ du commentaire agence sont déjà
  // persistées dans `external_comments` : elles ne repassent pas ici.
  const placedUrls = new Set<string>([
    ...Object.values(sellerFilesStructured).flat(),
    ...Object.values(buyerFilesStructured).flat(),
    ...Object.values(buyerResidencyFilesStructured).flat(),
    ...coBuyerFilesUrls,
    ...(agencyCommentFiles ?? []),
  ]);
  const otherFilesUrls = Array.from(
    new Set((allUploadedUrls ?? []).filter((url) => typeof url === 'string' && url.length > 0)),
  ).filter((url) => !placedUrls.has(url));

  return {
    ...flatForm,
    otherFilesUrls: JSON.stringify(otherFilesUrls),
    sellerFilesUrls: JSON.stringify(sellerFilesUrls),
    buyerFilesUrls: JSON.stringify(buyerFilesUrls),
    buyerResidencyFilesUrls: JSON.stringify(buyerResidencyFilesUrls),
    coBuyerFilesUrls: JSON.stringify(coBuyerFilesUrls),
    sellerFilesStructured: JSON.stringify(sellerFilesStructured),
    buyerFilesStructured: JSON.stringify(buyerFilesStructured),
    buyerResidencyFilesStructured: JSON.stringify(buyerResidencyFilesStructured),
    // Commentaire libre de l'agence + pièces jointes (persistés en
    // external_comments role=agency côté backend, affichés dans /review).
    agencyComment: agencyComment ?? '',
    agencyCommentFilesUrls: JSON.stringify(agencyCommentFiles ?? []),
    agencyID: agencyInfo.agencyID,
    personID: agencyInfo.personID,
    agencyEmail: agencyInfo.agencyEmail,
    agencyName: agencyInfo.agencyName,
    agencyAddress: agencyInfo.agencyAddress,
  };
};

/**
 * Extrait le département depuis un code postal (logique identique à `CGForm.tsx`).
 */
export const extractDepartmentFromPostalCode = (postalCode: string): string => {
  if (!postalCode || postalCode.length < 2) {
    throw new Error('Le code postal doit contenir au moins 2 chiffres pour déterminer le département');
  }
  const VALID_DOM_DEPARTMENTS = ['971', '972', '973', '974', '975', '976'];
  if (postalCode.startsWith('97')) {
    if (postalCode.length < 3) {
      throw new Error("Le code postal doit contenir au moins 3 chiffres pour les départements d'outre-mer");
    }
    const dept = postalCode.substring(0, 3);
    if (!VALID_DOM_DEPARTMENTS.includes(dept)) {
      throw new Error(`Le département ${dept} n'est pas pris en charge.`);
    }
    return dept;
  }
  const dept = postalCode.substring(0, 2);
  if (!/^\d{2}$/.test(dept)) {
    throw new Error('Le code postal doit commencer par 2 chiffres pour déterminer le département');
  }
  return dept;
};
